// Página pública de ativação de conta (PO1-2022).
//
// Recebe ?token=<jwt> via URL, valida política de senha (espelhando o backend
// PasswordPolicy: 8+ chars, 3 das 4 classes), submete em POST
// /auth/activation/confirm. O backend já faz login automático e devolve
// sessão pronta — salvamos no localStorage e recarregamos a página para que
// o AuthProvider pegue a sessão e roteie pra home autenticada.

import React, { useCallback, useRef, useState, useMemo } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Container,
  Col,
} from "reactstrap";
import { Link, useLocation } from "react-router-dom";
import NotificationAlert from "react-notification-alert";

import { Form } from "@unform/web";
import * as Yup from "yup";
import logo from "../../../assets/img/autopilot-logo.png";
import getValidationErrors from "../../../utils/getValidationErrors";
import CustomInputGroupText from "components/CustomInputGroupText";
import { useToast } from "../../../hooks/toast";
import { confirmActivation } from "services/usersApi";
import {
  TOKEN_KEY,
  REFRESH_KEY,
  REFRESH_EXP_KEY,
  USER_KEY,
} from "../../../services/authRefresh";
import "../Login/login.css";

function classesPresentes(senha) {
  let n = 0;
  if (/[a-z]/.test(senha)) n += 1;
  if (/[A-Z]/.test(senha)) n += 1;
  if (/[0-9]/.test(senha)) n += 1;
  if (/[^A-Za-z0-9]/.test(senha)) n += 1;
  return n;
}

const Activate = () => {
  const formRef = useRef(null);
  const alertRef = useRef();
  const { addToast } = useToast();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);

  const token = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("token");
  }, [location.search]);

  const handleSubmit = useCallback(
    async (data) => {
      try {
        formRef.current.setErrors({});

        const schema = Yup.object().shape({
          novaSenha: Yup.string()
            .required("Senha é obrigatória")
            .min(8, "A senha deve ter pelo menos 8 caracteres")
            .test(
              "complexidade",
              "Use pelo menos 3 dos 4: minúscula, maiúscula, número, símbolo",
              (v) => classesPresentes(v || "") >= 3
            ),
          confirmacao: Yup.string()
            .required("Confirme a nova senha")
            .oneOf([Yup.ref("novaSenha")], "As senhas não coincidem"),
        });
        await schema.validate(data, { abortEarly: false });

        if (!token) {
          addToast({
            place: "tr",
            type: "danger",
            title: "Token ausente",
            description: "Link de ativação inválido. Solicite um novo e-mail.",
            icon: "tim-icons icon-alert-circle-exc",
          });
          return;
        }

        setSubmitting(true);
        const result = await confirmActivation(token, data.novaSenha);

        // Caso 1: backend devolveu sessão completa → login automático.
        if (result.token && result.user) {
          localStorage.setItem(TOKEN_KEY, result.token);
          if (result.refreshToken) {
            localStorage.setItem(REFRESH_KEY, result.refreshToken);
          }
          if (result.refreshExpiresAt) {
            const iso =
              result.refreshExpiresAt instanceof Date
                ? result.refreshExpiresAt.toISOString()
                : new Date(result.refreshExpiresAt).toISOString();
            localStorage.setItem(REFRESH_EXP_KEY, iso);
          }
          localStorage.setItem(USER_KEY, JSON.stringify(result.user));
          // Reload força o AuthProvider a reler do storage e roteia pra home.
          window.location.href = "/";
          return;
        }

        // Caso 2: ativação OK mas login pós-ativação falhou (ex: gate Pages.HUB).
        // Direciona para login normal.
        addToast({
          place: "tr",
          type: "success",
          title: "Conta ativada",
          description: "Use sua nova senha para entrar.",
          icon: "tim-icons icon-check-2",
        });
        setTimeout(() => {
          window.location.href = "/auth/login";
        }, 1500);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRef.current.setErrors(errors);
          return;
        }
        const status = error.response && error.response.status;
        const data = (error.response && error.response.data) || {};
        const motivo = data.motivo;

        let description = data.error || "Não foi possível ativar a conta.";
        if (status === 400 && motivo === "expirado") {
          description = "Link expirado. Solicite um novo e-mail de ativação.";
        } else if (status === 400 && data.error === "token_invalido") {
          description = "Link inválido ou já utilizado.";
        }

        addToast({
          place: "tr",
          type: "danger",
          title: "Erro na ativação",
          description,
          icon: "tim-icons icon-alert-circle-exc",
        });
      } finally {
        setSubmitting(false);
      }
    },
    [token, addToast]
  );

  return (
    <div className="content">
      <div className="rna-container">
        <NotificationAlert ref={alertRef} />
      </div>
      <Container>
        <Col className="ml-auto mr-auto" lg="4" md="6">
          <Form
            ref={formRef}
            onSubmit={handleSubmit}
            className="form login-form"
          >
            <Card className="card-login card-white">
              <CardHeader>
                <img alt="..." src={logo} />
                <h2 className="card-title">Ativar conta</h2>
              </CardHeader>
              <CardBody>
                <p
                  style={{
                    color: "#666",
                    fontSize: 13,
                    textAlign: "center",
                    marginBottom: 16,
                  }}
                >
                  Defina sua senha para acessar o Everlog.
                </p>
                <CustomInputGroupText
                  name="novaSenha"
                  placeholder="Nova senha"
                  type="password"
                  styleInput="custom-input"
                  styleIcon="custom-input-icon"
                  iconClassName="tim-icons icon-lock-circle"
                />
                <CustomInputGroupText
                  name="confirmacao"
                  placeholder="Confirmar senha"
                  type="password"
                  styleInput="custom-input"
                  styleIcon="custom-input-icon"
                  iconClassName="tim-icons icon-lock-circle"
                />
                <p style={{ color: "#999", fontSize: 11, marginTop: 8 }}>
                  Mínimo 8 caracteres + pelo menos 3 das 4 classes
                  (minúscula, maiúscula, número, símbolo).
                </p>
              </CardBody>
              <CardFooter>
                <Button
                  block
                  className="mb-3 btn-primary"
                  type="submit"
                  disabled={submitting || !token}
                >
                  {submitting ? "Ativando…" : "Ativar conta"}
                </Button>
                <div className="pull-right">
                  <h6>
                    <Link to="/auth/login" className="link footer-link">
                      Voltar para o login
                    </Link>
                  </h6>
                </div>
              </CardFooter>
            </Card>
          </Form>
        </Col>
      </Container>
    </div>
  );
};

export default Activate;
